'use strict';

import { CONST, documentReady, assignStyling, formatToPriceString, arraysAreEqual, tryParseJSON } from 'Util/util';
import { setCookie, getCookie } from 'Util/cookies';
import * as debounce from 'lodash/debounce';

/**
 * @class VZPlpSidebar
 */
class VZPlpSidebar extends HTMLElement {
    constructor() {
        super();
        window.dataLayer = window.dataLayer || [];
    }

    /**
     * @function connectedCallback
     * @description Initializes the component and sets up event listeners
     * @returns {void}
     */
    connectedCallback() {
        // Sidebar
        this.sidebar = this;
        this.sidebarClasses = {
            open: 'plp-sidebar--open',
            closed: 'plp-sidebar--closed',
            lineItem: 'plp-sidebar__line-item',
            dependencyItem: 'plp-sidebar__dependency-item',
            removeItem: 'plp-sidebar__remove-item border-0 bg-white',
            adjustForSidebar: 'adjust-for-sidebar',
            hidden: 'd-none',
            hiddenOnEmpty: 'js-hidden-on-empty',
            visibleOnEmpty: 'js-visible-on-empty',
            hiddenOnClosed: 'js-hidden-on-closed'
        };

        // Basket data
        this.plpBasket = JSON.parse(this.getAttribute('plp-basket'));
        this.hasOneOffPrices = false;

        // Header
        this.pageHeader = document.querySelector('.js-navigation');
        this.filterBar = document.querySelector('.js-filter-bar');

        // Fields
        this.priceText = this.getAttribute('price-text');
        this.qtyText = this.getAttribute('qty-text');
        this.qtyTextSingular = this.getAttribute('qty-text-singular');
        this.emptyText = this.getAttribute('empty-text');
        this.labelText = this.getAttribute('label-text');

        // Content
        this.sidebarTitle = this.querySelector('.js-sidebar-title');
        this.sidebarContent = this.querySelector('.js-sidebar-content');
        this.sidebarTotal = this.querySelector('.js-sidebar-total');
        this.hiddenOnEmpty = this.sidebar.querySelectorAll(`.${this.sidebarClasses.hiddenOnEmpty}`);
        this.visibleOnEmpty = this.sidebar.querySelectorAll(`.${this.sidebarClasses.visibleOnEmpty}`);
        this.removeItemText = this.getAttribute('remove-item-text');

        // Styling
        this.sidebarTopOffset = 19;
        this.stickyBreakpoint = parseInt(CONST.breakpoints.lg, 10);

        // Disclaimers
        this.disclaimerOneOff = document.querySelector('.js-disclaimer-one-off');
        this.disclaimerOneOffMobile = document.querySelector('.js-disclaimer-one-off-mobile');

        // Mobile
        this.hiddenOnClosed = this.sidebar.querySelectorAll(`.${this.sidebarClasses.hiddenOnClosed}`);
        this.tapAreaFooter = document.querySelector('.js-card-footer-tap-area');
        this.tapAreaHeader = document.querySelector('.js-card-header-tap-area');

        // Chat
        this.chatButton = document.querySelector('.dimelo_chat_item_markup');

        // Methods
        this.initialize = this.initialize.bind(this);
        this.showLineItems = this.showLineItems.bind(this);
        this.updateHeader = this.updateHeader.bind(this);
        this.updateFooter = this.updateFooter.bind(this);
        this.deleteItem = this.deleteItem.bind(this);
        this.handleFloatingSidebar = this.handleFloatingSidebar.bind(this);
        this.handleFullOrEmptyState = this.handleFullOrEmptyState.bind(this);
        this.toggleDisclaimers = this.toggleDisclaimers.bind(this);
        this.calculateMaxHeightSidebarBody = this.calculateMaxHeightSidebarBody.bind(this);
        this.calculateMaxHeightMobileSidebarBody = this.calculateMaxHeightMobileSidebarBody.bind(this);
        this.openMobileSidebar = this.openMobileSidebar.bind(this);
        this.closeMobileSidebar = this.closeMobileSidebar.bind(this);
        this.tapAreaFooter.addEventListener('click', this.openMobileSidebar);
        this.tapAreaHeader.addEventListener('click', this.closeMobileSidebar);

        // Init
        this.initialize();

        // Cookies
        if (getCookie('plpBasket')) {
            this.setAttribute('plp-basket', decodeURIComponent(getCookie('plpBasket')));
            document.dispatchEvent(new CustomEvent('plp:basketUpdated')); // Trigger product tiles update
        }
    }

    openMobileSidebar() {
        this.sidebar.classList.remove(this.sidebarClasses.closed);
        this.sidebar.classList.add(this.sidebarClasses.open);

        // Update ARIA states for accessibility
        this.tapAreaHeader?.setAttribute('aria-expanded', 'true');
        this.tapAreaFooter?.setAttribute('aria-expanded', 'true');

        if (this.hiddenOnClosed.length > 0) {
            this.hiddenOnClosed.forEach((element) => {
                element.classList.remove(this.sidebarClasses.hidden);
            });
        }
    }

    closeMobileSidebar() {
        this.sidebar.classList.add(this.sidebarClasses.closed);
        this.sidebar.classList.remove(this.sidebarClasses.open);

        // Update ARIA states for accessibility
        this.tapAreaHeader?.setAttribute('aria-expanded', 'false');
        this.tapAreaFooter?.setAttribute('aria-expanded', 'false');

        if (this.hiddenOnClosed.length > 0) {
            this.hiddenOnClosed.forEach((element) => {
                element.classList.add(this.sidebarClasses.hidden);
            });
        }
    }

    /**
     * @function disconnectedCallback
     * @description Removes event listeners when the component is disconnected from the DOM
     * @returns {void}
     */
    disconnectedCallback() {
        Array.from(this.querySelectorAll('.js-remove-item')).forEach((deleteIcon) => {
            deleteIcon.removeEventListener('click', this.deleteItem);
        });
    }

    /**
     * @function observedAttributes
     * @description observes the mentioned attribute and fires attributeChangedCallback when the value has changed
     * @returns {Attr} this attribute's value is an encoded JSON string so should be decoded when using.
     */
    static get observedAttributes() {
        return ['plp-basket'];
    }

    /**
     * @function attributeChangedCallback
     * @description fired when an observed attribute changes
     * @param {string} name the name of the attribute
     * @param {any} oldValue the previous value of the attribute
     * @param {any} newValue the new value of the attribute
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'plp-basket' && oldValue !== newValue && this.sidebarContent) {
            const oldBasket = tryParseJSON(oldValue);
            const newBasket = tryParseJSON(newValue);

            this.constructor.trackChanges(oldBasket, newBasket);
            this.plpBasket = newBasket;

            setCookie('plpBasket', encodeURIComponent(newValue), window.location.hostname, 30);

            this.initialize();
        }
    }

    /**
     * Displays line items in the sidebar.
     * @function showLineItems
     * @returns {void}
     */
    showLineItems() {
        const productsMarkup = this.plpBasket.reduce((result, product) => {
            const qty = parseInt(product.qty, 10);
            const costs = parseFloat(product.price, 10) * qty;
            const startingPrice = parseFloat(product.startingPrice || product.price, 10);
            const startingCosts = startingPrice * qty;
            const hasDiscount = startingPrice > parseFloat(product.price, 10);

            const label = product.isDependent
                ? `<span class="${this.sidebarClasses.dependencyItem}">
                        ${this.labelText}
                    </span>
                `
                : `
                    <button class="${this.sidebarClasses.removeItem}" aria-label="${this.removeItemText}">
                        <div class="icon icon-delete svg-icon-sm js-remove-item" aria-hidden="true"></div>
                    </button>
                `;

            const priceMarkup = hasDiscount
                ? `<span class="small">
                        ${this.priceText} <span class="strike-through">${formatToPriceString(startingCosts)}</span> <strong class="text-turquoise">${formatToPriceString(costs)}</strong>
                    </span>`
                : `<span class="small">
                        ${this.priceText} ${formatToPriceString(costs)}
                    </span>`;

            return `
                ${result}
                <li class="${this.sidebarClasses.lineItem} mb-2 js-line-item" data-product-id="${product.id}">
                    <div class="d-flex justify-content-between align-items-center">
                        <span class="h6 m-0">
                            ${product.name} x ${qty}
                        </span>
                        ${label}
                    </div>
                    ${priceMarkup}
                </li>
            `;
        }, '');

        // Update card body with line items
        this.sidebarContent.innerHTML = productsMarkup;

        // Add event listener for line item removal
        Array.from(this.querySelectorAll('.js-remove-item')).forEach((deleteIcon) => {
            deleteIcon.addEventListener('click', this.deleteItem);
        });

        this.updateHeader();
        this.updateFooter();
    }

    /**
     * Toggles the disclaimer content based on selected products.
     * @function toggleDisclaimers
     * @returns {void}
     */
    toggleDisclaimers() {
        const isEmptyBasket = this.plpBasket.length === 0;

        this.hasOneOffPrices = this.plpBasket.filter((product) => product.oneOffPrice !== 'null' && product.oneOffPrice !== '0.00').length > 0;

        this.disclaimerOneOffMobile?.classList.add(this.sidebarClasses.hidden);
        this.disclaimerOneOff?.classList.add(this.sidebarClasses.hidden);

        // Don't show disclaimers if basket is empty or no selected products have one-off prices
        if (isEmptyBasket) return;

        if (window.innerWidth < this.stickyBreakpoint) {
            this.disclaimerOneOffMobile?.classList.toggle(this.sidebarClasses.hidden, !this.hasOneOffPrices);
        } else {
            this.disclaimerOneOff?.classList.toggle(this.sidebarClasses.hidden, !this.hasOneOffPrices);
        }
    }

    /**
     * Updates the footer content based on selected products.
     * @function updateFooter
     * @returns {void}
     */
    updateFooter() {
        // Calculate total price
        const totalPrice = this.plpBasket.reduce((total, product) => total + parseFloat(product.price, 10) * parseInt(product.qty, 10), 0);

        // Update footer total text
        this.sidebarTotal.innerHTML = formatToPriceString(totalPrice);
    }

    /**
     * Updates the header content based on selected products.
     * @function updateHeader
     * @returns {void}
     */
    updateHeader() {
        // Calculate number of line items
        const numberProductsSelected = parseInt(this.plpBasket.length, 10);

        // Select singular or plural text based on count
        const qtyLabel = numberProductsSelected === 1 ? this.qtyTextSingular : this.qtyText;

        // Update card header with correct text
        this.sidebarTitle.innerHTML = numberProductsSelected === 0 ? this.emptyText : qtyLabel.replace('{0}', numberProductsSelected);
    }

    /**
     * Deletes a line item from the sidebar.
     * @function deleteItem
     * @param {Event} e - The click event.
     * @returns {void}
     */
    deleteItem(e) {
        const currentLineItem = e.target.closest('.js-line-item');

        if (currentLineItem) {
            const productIdToRemove = currentLineItem.dataset.productId;
            const productToRemove = this.plpBasket.find((product) => product.id === productIdToRemove);
            const dependencyProductId = productToRemove?.dependency;

            this.plpBasket = this.plpBasket.filter((product) => product.id !== productIdToRemove && product.id !== dependencyProductId);

            this.setAttribute('plp-basket', JSON.stringify(this.plpBasket));

            document.dispatchEvent(new CustomEvent('plp:basketUpdated')); // Trigger product tiles update
        }
    }

    /**
     * Initializes the sidebar component.
     * @function initialize
     * @returns {void}
     */
    initialize() {
        if (this.plpBasket.length > 0) this.showLineItems();

        this.handleFullOrEmptyState();

        documentReady(this.handleFloatingSidebar);

        window.addEventListener(
            'resize',
            debounce(() => {
                if (window.innerWidth < this.stickyBreakpoint) {
                    this.closeMobileSidebar();
                } else {
                    this.openMobileSidebar();
                }

                this.handleFloatingSidebar();
                this.toggleDisclaimers();
            }),
            150
        );
    }

    /**
     * Handles the appearance of the floating sidebar at certain resolutions.
     * @function handleFloatingSidebar
     * @returns {void}
     */
    handleFullOrEmptyState() {
        const isEmptyBasket = this.plpBasket.length === 0;

        this.updateHeader();
        this.toggleDisclaimers();

        // Toggle elements only visible when basket is empty
        if (this.visibleOnEmpty.length > 0) {
            this.visibleOnEmpty.forEach((element) => {
                element.classList.toggle(this.sidebarClasses.hidden, !isEmptyBasket);
            });
        }

        // Toggle elements only visible when basket is NOT empty
        if (this.hiddenOnEmpty.length > 0) {
            this.hiddenOnEmpty.forEach((element) => {
                element.classList.toggle(this.sidebarClasses.hidden, isEmptyBasket);
            });
        }

        // Adjust chat button for mobile sidebar
        if (this.chatButton && window.innerWidth < this.stickyBreakpoint) this.chatButton?.classList.toggle(this.sidebarClasses.adjustForSidebar, !isEmptyBasket);
    }

    /**
     * @function handleFloatingSidebar
     * @private
     * @description Handles the styling required for the sidebar to become sticky at certain resolutions
     */
    handleFloatingSidebar() {
        const isEmptyBasket = this.plpBasket.length === 0;

        this.sidebar.classList.toggle(this.sidebarClasses.hidden, window.innerWidth < this.stickyBreakpoint && isEmptyBasket);

        if (window.innerWidth < this.stickyBreakpoint) {
            // Adjust and display chat button for mobile
            if (this.chatButton) this.chatButton?.classList.toggle(this.sidebarClasses.adjustForSidebar, !isEmptyBasket);

            this.sidebar.style.removeProperty('position');
            this.sidebar.style.removeProperty('top');
            this.sidebar.style.removeProperty('bottom');
            this.sidebar.style.removeProperty('width');

            // Mobile sidebar is sticky to the bottom
            this.sidebar.style.removeProperty('top');

            assignStyling(this.sidebar, {
                position: 'fixed',
                bottom: '0',
                left: '0',
                right: '0'
            });

            if (!isEmptyBasket) {
                assignStyling(this.sidebarContent, {
                    'max-height': `${this.calculateMaxHeightMobileSidebarBody()}px`
                });
            }

            if (this.sidebar.classList.contains(this.sidebarClasses.open)) {
                this.openMobileSidebar();
            } else {
                this.closeMobileSidebar();
            }
        } else {
            // Adjust and display chat button for desktop
            if (this.chatButton) this.chatButton.classList.remove(this.sidebarClasses.adjustForSidebar, this.sidebarClasses.hidden);

            this.filterBar = document.querySelector('.js-filter-bar');

            this.openMobileSidebar();

            // Desktop sidebar is sticky to the right column
            this.sidebar.style.removeProperty('bottom', 'left', 'right');

            assignStyling(this.sidebar, {
                position: 'sticky',
                top: `${parseInt(this.pageHeader.offsetHeight, 10) + (this.filterBar ? parseInt(this.filterBar.parentElement.offsetHeight, 10) : this.sidebarTopOffset)}px`
            });

            if (!isEmptyBasket) {
                assignStyling(this.sidebarContent, {
                    'max-height': `${this.calculateMaxHeightSidebarBody()}px`
                });
            }
        }
    }

    /**
     * Calculates the maximum height of the sidebar body.
     * @function calculateMaxHeightSidebarBody
     * @returns {number} - The calculated maximum height.
     */
    calculateMaxHeightSidebarBody() {
        // Line item total height
        const lineItem = this.querySelector('.js-line-item');
        const lineItemHeight = lineItem.clientHeight > 0 ? lineItem.clientHeight : 56; // 3.5rem
        const lineItemSpacingType = window.getComputedStyle(lineItem).getPropertyValue('margin-bottom');
        const lineItemSpacing = lineItemSpacingType ? lineItemSpacingType.replace('px', '') : 16; // 1rem
        const lineItemTotalHeight = lineItemHeight + parseInt(lineItemSpacing, 10);
        const lineItemsToShow = 3.5;
        const lastLineItemHeightToRemove = window.innerWidth < this.stickyBreakpoint ? 8 : 4; // 0.5rem || 0.25rem

        //  + extraLineItemHeight
        return lineItemTotalHeight * lineItemsToShow - lastLineItemHeightToRemove;
    }

    /**
     * Calculates the maximum height of the mobile sidebar body.
     * @function calculateMaxHeightMobileSidebarBody
     * @returns {number} - The calculated maximum height.
     */
    calculateMaxHeightMobileSidebarBody() {
        // Same as desktop sidebar (BO-3191)
        return this.calculateMaxHeightSidebarBody();
    }

    /**
     * Tracks plp basket changes and logs them to the dataLayer.
     *
     * @param {Array} oldArray - The original plp basket.
     * @param {Array} newArray - The updated plp basket.
     */
    static trackChanges(oldArray, newArray) {
        // If new basket set from cookies, do not track as it is initial page load
        if (getCookie('plpBasket')) {
            const savedBasket = tryParseJSON(decodeURIComponent(getCookie('plpBasket')));

            if (arraysAreEqual(savedBasket, newArray)) return;
        }

        const oldMap = new Map(oldArray.map((item) => [item.id, item]));
        const newMap = new Map(newArray.map((item) => [item.id, item]));

        // Track added and updated products
        newArray.forEach((newItem) => {
            const oldItem = oldMap.get(newItem.id) || {
                qty: 0,
                name: newItem.name
            };

            if (oldItem.qty !== newItem.qty) {
                const change = newItem.qty - oldItem.qty;

                if (change > 0) {
                    window.dataLayer.push({
                        event: 'mkb_solutions_cart',
                        solutions_product: oldItem.name,
                        solutions_cart: 'increased'
                    });
                } else if (change < 0) {
                    window.dataLayer.push({
                        event: 'mkb_solutions_cart',
                        solutions_product: oldItem.name,
                        solutions_cart: 'decreased'
                    });
                }
            }
        });

        // Track removed products
        oldArray.forEach((oldItem) => {
            if (!newMap.has(oldItem.id)) {
                window.dataLayer.push({
                    event: 'mkb_solutions_cart',
                    solutions_product: oldItem.name,
                    solutions_cart: 'removed'
                });
            }
        });
    }
}

export default window.customElements.get('vz-plp-sidebar') || VZPlpSidebar;

if (!window.customElements.get('vz-plp-sidebar')) {
    window.customElements.define('vz-plp-sidebar', VZPlpSidebar);
}
